// src/api/propertyApi.js
import axios from "axios";
import axiosClient from "./axiosClient";

const IDEAL_POSTCODES_API_KEY =
  process.env.REACT_APP_IDEAL_POSTCODES_API_KEY ||
  process.env.REACT_APP_GETADDRESS_API_KEY;
const IDEAL_POSTCODES_BASE_URL =
  process.env.REACT_APP_IDEAL_POSTCODES_BASE_URL ||
  "https://api.ideal-postcodes.co.uk/v1";
const addressLookupClient = axios.create({
  withCredentials: false,
});

const getAddressLookupConfig = () => {
  if (!IDEAL_POSTCODES_API_KEY) {
    throw new Error(
      "Missing REACT_APP_IDEAL_POSTCODES_API_KEY (or REACT_APP_GETADDRESS_API_KEY) for address lookup."
    );
  }
  return {
    apiKey: IDEAL_POSTCODES_API_KEY,
    baseUrl: IDEAL_POSTCODES_BASE_URL.replace(/\/+$/, ""),
  };
};

const toDisplayAddress = (address) =>
  [
    address?.line_1,
    address?.line_2,
    address?.line_3,
    address?.post_town || address?.town_or_city,
    address?.postcode,
  ]
    .filter(Boolean)
    .join(", ");

const normalizeSuggestion = (item) => {
  const id = item?.id || item?.udprn || item?.uprn || "";
  const address =
    item?.address ||
    item?.suggestion ||
    item?.label ||
    item?.text ||
    toDisplayAddress(item);
  const uprn = item?.uprn || item?.udprn || "";
  return {
    id: String(id),
    address,
    uprn: uprn ? String(uprn) : "",
  };
};

const normalizeAddress = (address = {}) => ({
  ...address,
  ideal_postcodes_id: String(address.id || address.udprn || address.uprn || ""),
  uprn: address.uprn ? String(address.uprn) : "",
  town_or_city: address.town_or_city || address.post_town || "",
  locality: address.locality || address.dependant_locality || "",
});

// Create a new property
export const createProperty = (data) =>
  axiosClient.post("/properties/create/", data);

// Fetch property by ID
export const getPropertyById = async (propertyId) => {
  const response = await axiosClient.get(`/properties/${propertyId}/`);
  return response.data;
};

// Update an existing property
export const updateProperty = async (propertyId, data) => {
  const response = await axiosClient.put(
    `/properties/${propertyId}/update/`,
    data
  );
  return response.data;
};

// Fetch properties accessible to user
export const getAccessibleProperties = async (liveFilter = null) => {
  const params = {};
  if (liveFilter !== null) {
    params.live = liveFilter;
  }
  const response = await axiosClient.get("/properties/accessible/", { params });
  return response.data;
};

// Fetch full property details
export const getPropertyDetails = async (propertyId, params) => {
  const response = await axiosClient.get(
    `/properties/${propertyId}/details/`,
    { params }
  );
  return response.data;
};

export const getPropertyDirectors = async (propertyId) => {
  const response = await axiosClient.get(`/properties/${propertyId}/directors/`);
  return response.data;
};

export const createPropertyDirector = async (propertyId, data) => {
  const response = await axiosClient.post(`/properties/${propertyId}/directors/`, data);
  return response.data;
};

export const updatePropertyDirector = async (propertyId, directorId, data) => {
  const response = await axiosClient.put(
    `/properties/${propertyId}/directors/${directorId}/`,
    data
  );
  return response.data;
};

export const deletePropertyDirector = async (propertyId, directorId) => {
  const response = await axiosClient.delete(
    `/properties/${propertyId}/directors/${directorId}/`
  );
  return response.data;
};

export const getDirectorsEmailLink = async (propertyId) => {
  const response = await axiosClient.get(
    `/properties/${propertyId}/directors/email/`
  );
  return response.data;
};

// Fetch assessment types
export const getAssessmentTypes = async () => {
  const response = await axiosClient.get("/assessment-types/");
  return response.data;
};

// Fetch all properties (admin)
export const getAllProperties = async () => {
  const res = await axiosClient.get("/properties/");
  return res.data;
};

// -------------------------------------------------------------
// 🔹 FETCH PROPERTY ENQUIRY FORMS FOR A PROPERTY (ALL UNITS)
// -------------------------------------------------------------
export const getPropertyLPE1Forms = async (propertyId, params = {}) => {
  const response = await axiosClient.get(
    `/properties/${propertyId}/lpe1/`,
    { params }
  );
  return response.data;
};

export const updatePropertyMetadata = (propertyId, metadata, options = {}) => {
  return axiosClient.patch(
    `/properties/${propertyId}/metadata/`,
    metadata,
    { params: options }
  );
};

export const getPortalWideLpe1Section12Defaults = async () => {
  const response = await axiosClient.get("/portal-defaults/lpe1/section-1-2/");
  return response.data?.metadata || {};
};

export const updatePortalWideLpe1Section12Defaults = (metadata) => {
  return axiosClient.patch("/portal-defaults/lpe1/section-1-2/", metadata);
};

// Search properties (server-side search)
export const searchProperties = async (query, limit = 20, liveFilter = null) => {
  const response = await axiosClient.get("/properties/search/", {
    params: { q: query, limit, ...(liveFilter !== null ? { live: liveFilter } : {}) },
  });
  return response.data;
};

export const lookupAddress = async (postcode, filter = "") => {
  const { apiKey, baseUrl } = getAddressLookupConfig();
  const query = [postcode, filter].filter(Boolean).join(" ");
  let response;
  try {
    response = await addressLookupClient.get(
      `${baseUrl}/autocomplete/addresses`,
      {
        params: {
          api_key: apiKey,
          q: query,
          postcode,
        },
      }
    );
  } catch (error) {
    const payload = error?.response?.data;
    const message =
      payload?.message ||
      payload?.error?.message ||
      payload?.error ||
      error?.message ||
      "Address lookup failed.";
    throw new Error(message);
  }

  const payload = response?.data;
  let suggestions =
    payload?.result?.hits || payload?.result || payload?.hits || [];
  if (!Array.isArray(suggestions)) {
    suggestions = [];
  }

  suggestions = suggestions.map(normalizeSuggestion).filter((item) => item.id);
  if (filter.trim()) {
    const needle = filter.toLowerCase();
    suggestions = suggestions.filter((suggestion) =>
      (suggestion.address || "").toLowerCase().includes(needle)
    );
  }
  return { suggestions };
};

export const getAddressById = async (id) => {
  const { apiKey, baseUrl } = getAddressLookupConfig();
  const response = await addressLookupClient.get(
    `${baseUrl}/autocomplete/addresses/${encodeURIComponent(id)}/gbr`,
    {
      params: { api_key: apiKey },
    }
  );

  const payload = response?.data;
  const address = payload?.result || payload?.address || payload || {};
  return normalizeAddress(address);
};
